import * as React from 'react';

import { Dispatch } from 'redux';
import { useLocation } from 'react-router';
import { connect, useSelector } from 'react-redux';

import { isPassword } from '@webapp/utils/validators/index';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import { ForgotPasswordStart } from '@webapp/store/auth/action-creators';
import { ForgotPassword } from '@webapp/store/auth/action-creators';
import {
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_ERROR,
} from '@webapp/store/auth/actions';
import { ResetError } from '@webapp/store/ui/action-creators';

import ForgotPasswordData from '@webapp/types/auth/forgot-password-data';

import PasswordCtrl from '../password';

import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import ButtonArrow from '@components/actions/arrow-button';
import Hint, { HintText, HintTitle } from '@components/basics/hint';
import Typography from '@components/basics/typography';
import SnackbarStatus from '@components/basics/snackbar/snackbar-status';
import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';

interface ConfirmForgotPasswordCtrlProps {
  username: string;
  className?: string;
  isLoading: boolean;
  isMobile: boolean;
  confirmForgotPassword: (data: ForgotPasswordData) => ForgotPassword;
  forgotPassword: (username: string) => ForgotPasswordStart;
  resetForgetPasswordError: () => ResetError;
}

const mapStateToProps = (state: AppState) => ({
  isLoading: !!state.ui.loading[FORGOT_PASSWORD],
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  confirmForgotPassword: (data: ForgotPasswordData) =>
    dispatch(new ForgotPassword(data)),
  forgotPassword: (username: string) =>
    dispatch(new ForgotPasswordStart(username)),
  resetForgetPasswordError: () =>
    dispatch(new ResetError(FORGOT_PASSWORD_ERROR)),
});

export const ConfirmForgotPasswordCtrl: React.FC<
  ConfirmForgotPasswordCtrlProps
> = (props) => {
  const urlParams = new URLSearchParams(useLocation().search);
  const verificationCode = urlParams.get('code');

  const [code, setCode] = React.useState(verificationCode || '');
  const [newPassword, setNewPassword] = React.useState('');
  const [emptyCode, setEmptyCode] = React.useState(false);
  const [emptyPassword, setEmptyPassword] = React.useState(false);
  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    if (code.length === 0) {
      setEmptyCode(true);
    }
    if (newPassword.length === 0) {
      setEmptyPassword(true);
    }
    if (!isPassword(newPassword)) {
      return;
    }
    if (code && newPassword) {
      props.confirmForgotPassword({
        username: props.username,
        code,
        newPassword,
      });

      setCode('');
      setNewPassword('');
    }
  };

  const passwordError = useSelector(
    (state: AppState) => !!state.ui.errors[FORGOT_PASSWORD_ERROR]
  );

  // TODO: DEVTEAM-1057 - handleResendCode needs to be integrated.
  const handleResendCode = () => {
    props.forgotPassword(props.username);
    setShowNotification(true);
  };

  return (
    <form
      autoComplete="off"
      className={props.className}
      onSubmit={(ev) => handleSubmit(ev)}
    >
      <Flex>
        <Flex
          className="wrapper"
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          <Grid className="container" gap={10}>
            <InputField
              isGrey
              name="code"
              label={<Translate id="AUTH.VERIFY.INPUT.CODE" />}
              autoComplete="code"
              value={code}
              error={
                passwordError || emptyCode ? (
                  <Translate id="CodeMismatchException" />
                ) : undefined
              }
              onChange={(value) => {
                if (passwordError) {
                  props.resetForgetPasswordError();
                }
                setCode(value);
                setEmptyCode(false);
              }}
            />

            <PasswordCtrl
              label={<Translate id="AUTH.VERIFY.INPUT.NEW_PASSWORD" />}
              autoComplete="new-password"
              isGrey
              password={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                setEmptyPassword(false);
              }}
              error={
                emptyPassword ? (
                  <Translate id="AUTH.VERIFY.INPUT.INVALID_PASSWORD" />
                ) : undefined
              }
            />
          </Grid>
          <ButtonArrow
            type="submit"
            id="forgotPassword"
            className="button-arrow"
            isLoading={!!props.isLoading}
            disabled={!code || !newPassword}
          />
        </Flex>
      </Flex>
      <Hint
        noTitle
        align="left"
        customContent={
          <Flex
            gap={20}
            alignItems="center"
            justifyContent="space-between"
            flexDirection={props.isMobile ? 'column' : 'row'}
          >
            <Grid gap={5}>
              <HintTitle weight="700">
                <Translate id="AUTH.VERIFY.HINT.TITLE" />
              </HintTitle>
              <HintText>
                <Translate id="AUTH.VERIFY.HINT.TEXT" />
              </HintText>
            </Grid>
            <Link weight="700" onClick={handleResendCode}>
              <Translate id="AUTH.VERIFY.HINT.LINK" />
            </Link>
          </Flex>
        }
      />
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status="success"
            title={<Translate id="AUTH.VERIFY.HINT.LINK.SENT" />}
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
    </form>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ConfirmForgotPasswordCtrl)`
  .wrapper {
    width: 100%;
  }
  .container {
    flex-grow: 1;
  }
  .button-arrow {
    max-width: 78px;
  }
`);

const Link = styled(Typography)`
  flex-shrink: 0;
  cursor: pointer;
  opacity: 1;
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }
`;
